import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { MessageService } from 'primeng/api'

import { AuthService } from '@data/services/modules/auth/auth.service'
import { ApiInvoiceService } from '../../data/services/api/api-invoice.service'

// Modal "Complementos de pago (REP)" de una factura PPD: lista los complementos ligados y permite
// cargar nuevos REP (PDF/XML), que se ligan por el UUID de las facturas relacionadas.
@Component({
	selector: 'app-invoice-complements-dialog',
	templateUrl: './invoice-complements-dialog.component.html',
	styleUrls: ['./invoice-complements-dialog.component.scss'],
	providers: [MessageService],
})
export class InvoiceComplementsDialogComponent implements OnChanges {
	@Input() visible = false
	@Input() invoice: any = null
	@Output() visibleChange = new EventEmitter<boolean>()
	@Output() changed = new EventEmitter<void>() // tras ligar un REP → refrescar dashboard

	complements: any[] = []
	loading = false
	selectedFiles: File[] = []
	uploading = false
	isDragging = false

	constructor(
		private readonly api: ApiInvoiceService,
		private readonly messageService: MessageService,
		private readonly authService: AuthService,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['visible'] && this.visible && this.invoice?._id) this.load()
	}

	private get userId(): string | undefined {
		return this.authService.getUser?.user?.id
	}

	load(): void {
		if (!this.invoice?._id) return
		this.loading = true
		this.api.getComplements(this.invoice._id).subscribe({
			next: (r) => {
				this.loading = false
				this.complements = r.data || []
			},
			error: () => {
				this.loading = false
				this.complements = []
			},
		})
	}

	onHide(): void {
		this.visibleChange.emit(false)
		this.selectedFiles = []
		this.isDragging = false
	}
	close(): void {
		this.visible = false
		this.visibleChange.emit(false)
	}

	onFilesSelected(event: Event): void {
		const input = event.target as HTMLInputElement
		this.selectedFiles = input.files ? Array.from(input.files) : []
	}
	onDragOver(event: DragEvent): void {
		event.preventDefault()
		this.isDragging = true
	}
	onDragLeave(event: DragEvent): void {
		event.preventDefault()
		this.isDragging = false
	}
	onDrop(event: DragEvent): void {
		event.preventDefault()
		this.isDragging = false
		const dropped = event.dataTransfer?.files
		if (dropped && dropped.length) this.selectedFiles = Array.from(dropped).filter((f) => /\.(pdf|xml)$/i.test(f.name))
	}

	upload(): void {
		if (!this.selectedFiles.length) {
			this.toast('warn', 'Sin archivos', 'Selecciona los PDF/XML del complemento (REP).')
			return
		}
		this.uploading = true
		this.api.uploadComplements(this.selectedFiles, this.userId).subscribe({
			next: (r) => {
				this.uploading = false
				if (r.error || !r.data) {
					this.toast('error', 'Error al subir', r.msg)
					return
				}
				this.toast('success', 'Complementos procesados', `${r.data.processed ?? 0} REP procesado(s).`)
				this.selectedFiles = []
				this.load()
				this.changed.emit()
			},
			error: () => {
				this.uploading = false
				this.toast('error', 'Error', 'Falló la carga de complementos.')
			},
		})
	}

	downloadFile(complementId: string, kind: 'pdf' | 'xml'): void {
		this.api.downloadComplementFile(complementId, kind).subscribe({
			next: (res) => {
				const blob = res.body as Blob | null
				if (!blob) return
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = this.filenameFromResponse(res) || `complemento.${kind}`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				setTimeout(() => URL.revokeObjectURL(url), 1000)
			},
			error: () => this.toast('error', 'Complemento', `No se pudo descargar el ${kind.toUpperCase()}.`),
		})
	}

	private filenameFromResponse(res: any): string | null {
		const cd = res?.headers?.get?.('content-disposition') || res?.headers?.get?.('Content-Disposition')
		if (!cd) return null
		const star = /filename\*=UTF-8''([^;]+)/i.exec(cd)
		if (star) return decodeURIComponent(star[1])
		const m = /filename="?([^";]+)"?/i.exec(cd)
		return m ? m[1] : null
	}

	// Documentos relacionados de un REP que corresponden a ESTA factura (para mostrar monto/fecha).
	relatedForInvoice(comp: any): any[] {
		const invId = this.invoice?._id
		return (comp?.relatedDocs || []).filter((d: any) => String(d.invoice) === String(invId))
	}

	// REP invalidado: el pago que documentaba se canceló → conservado como historial, debe cancelarse ante SAT.
	isInvalidated(comp: any): boolean {
		return comp?.status === 'INVALIDATED'
	}

	// Nombre del usuario que invalidó el REP (invalidatedBy viene poblado desde getByInvoice).
	userName(u: any): string {
		if (!u || typeof u !== 'object') return ''
		return [u.names, u.lastNames].filter(Boolean).join(' ').trim()
	}

	private toast(severity: string, summary: string, detail: string): void {
		this.messageService.add({ severity, summary, detail, life: severity === 'error' ? 8000 : 5000 })
	}
}
